import { useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { compressImage, COMPRESSION_PRESETS } from "@/lib/imageCompression";
import { toast } from "sonner";

export interface PhotoInputButtonProps {
  onImageSelected: (imageData: { base64: string; mimeType: string }) => void;
  disabled?: boolean;
}

/**
 * PhotoInputButton — camera/file picker for attaching photos to agent chat messages.
 *
 * On mobile, `capture="environment"` opens the rear camera directly.
 * Selected images are compressed with the defect preset before being converted to base64.
 */
export function PhotoInputButton({ onImageSelected, disabled }: PhotoInputButtonProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isCompressing, setIsCompressing] = useState(false);

  const handleButtonClick = () => {
    if (!disabled && !isCompressing) {
      inputRef.current?.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so the same file can be selected again
    e.target.value = "";

    setIsCompressing(true);
    try {
      const { file: compressedFile } = await compressImage(file, COMPRESSION_PRESETS.defect);

      await new Promise<void>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          // Strip the "data:<mimeType>;base64," prefix to get raw base64
          const base64 = dataUrl.split(",")[1];
          const mimeType = compressedFile.type || "image/jpeg";
          onImageSelected({ base64, mimeType });
          resolve();
        };
        reader.onerror = () => reject(new Error("FileReader failed"));
        reader.readAsDataURL(compressedFile);
      });
    } catch {
      toast.error(t("inspectionChat.photo.errorCompress"));
    } finally {
      setIsCompressing(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
        aria-hidden="true"
        tabIndex={-1}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleButtonClick}
        disabled={disabled || isCompressing}
        aria-label={t("inspectionChat.photo.attach")}
        className="shrink-0"
      >
        {isCompressing ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Camera className="h-4 w-4" />
        )}
      </Button>
    </>
  );
}
